import {AttributeLink, AttributeSetup, getConfig, getTarget} from '@acng/frontend-stargazer';
import {Watch} from '@acng/frontend-relativity';
import {IS, NULLABLE, OBJECT, OPTIONAL, STRING, guard} from '@acng/frontend-rubicon';
// bounty
import {undefined} from '@acng/frontend-bounty/std/value.js';
import {remove} from '@acng/frontend-bounty/dom/move.js';
// prettier-ignore
import {CANPLAYTHROUGH, ENDED, MOUSEENTER, MOUSELEAVE, off, on, once, PAUSE} from '@acng/frontend-bounty/dom/event.js';
import {Closest, MUTED, LOOP, PLAYSINLINE, SRC, unset} from '@acng/frontend-bounty/dom/attribute.js';
import {isConnected} from '@acng/frontend-bounty/dom/type.js';
import {has as hasClass, remove as removeClass} from '@acng/frontend-bounty/style/element.js';
import {debug} from '@acng/frontend-bounty/dom/debug.js';
import {onClick} from '@acng/frontend-bounty/dom/document.js';
import {MOVIE_PREVIEW_AUTOPLAY} from './movie-preview-autoplay.js';
import {debounce} from '@acng/frontend-bounty/timing/debounce.js';
import {URL} from '@acng/frontend-bounty/net/url.js';

import {media} from '../../core/service/env.js';
import {ctxListPosition} from '../../layout/context/list-position.js';
import {animate} from '../../layout/service/animate-class.js';
import {getMovie} from '../context/movie.js';
import {compile} from '../service/template.js';
import {PreviewWatched} from 'acng/Lib/Log/events/previewWatched.js';
import {logger} from 'acng/Lib/Log/client.js';
import {popup} from '@acng/frontend-discovery';
import {user} from '../../user/user.js';

/**
 * @group DOM Attribute
 */
export const MOVIE_PREVIEW = 'movie-preview';

const IS_HTML_VIDEO = /* @__PURE__ */ IS(HTMLVideoElement);
const IS_HTML = /* @__PURE__ */ IS(HTMLElement);

AttributeSetup(MOVIE_PREVIEW, (name) => {
  /**
   * The hovered `<video>`
   *
   * > The first synchronous phase.
   *
   * @type {HTMLVideoElement | undefined}
   */
  let hover;

  /**
   * The autoplay `<video>`
   *
   * > The second synchronous phase.
   *
   * @type {HTMLVideoElement | undefined}
   */
  let autoplay;

  /**
   * Allow only one `<video>` to load only once.
   *
   * > The outer asynchronous phase.
   *
   * @type {HTMLVideoElement | undefined}
   */
  let startedloading;

  const watch = Watch(`[${name}]`, ctxListPosition);

  const ifAutoplayIsAllowed = Closest(MOVIE_PREVIEW_AUTOPLAY);

  const load = debounce(
    /**
     * @param {HTMLVideoElement} video
     * @param {string} src
     */
    (video, src) => {
      if ((!hover && !autoplay) || startedloading == video || !isConnected(video)) {
        return;
      }

      stop();
      once(video, 'error', () => popup(video).error(src));
      video[SRC] = src;
      video[PLAYSINLINE] = true;
      video[MUTED] = true;
      video.load();
      startedloading = video;
      once(video, CANPLAYTHROUGH, play);
    },
    333,
  );

  const stop = async () => {
    if (!startedloading) {
      return;
    }

    const video = startedloading;

    DEBUG: if (debug(video)) console.log('stop movie preview', {video});
    off(video, CANPLAYTHROUGH, play);
    startedloading = undefined;

    if (!hasClass(video, 'show-active')) {
      removeClass(video, 'show');
      await animate(video, 'hide');

      if (startedloading == video) {
        return;
      }
    }

    //video[HIDDEN] = true;
    removeClass(video, 'show', 'hide');
    video.pause();
    unset(video, SRC);
  };

  /**
   * @param {Event} evt
   */
  const play = async ({target: video}) => {
    ASSERT: guard(video, IS_HTML_VIDEO);
    DEBUG: console.debug('play', {enabled: hover, loading: startedloading, video});

    if (startedloading != video || !isConnected(video)) {
      return;
    }

    removeClass(video, 'hide', 'hide-active');

    try {
      //video[HIDDEN] = false;
      await video.play();
      animate(video, 'show', true);

      const gallery = getMovie(video);
      const logEvent = new PreviewWatched(gallery);
      once(video, PAUSE, () => logger.send(logEvent.end()));
      once(video, ENDED, stop);
    } catch (reason) {
      console.error(reason);
      popup(video).error(`${reason}`);
    }
  };

  onClick(stop);

  AttributeLink(name, (video, previewMode) => (_video, engine) => {
    const listElement = getTarget(engine);
    const preview = getConfig(engine).video;

    ASSERT: {
      guard(video, IS_HTML_VIDEO);
      guard(listElement, IS_HTML);
      guard(preview, OPTIONAL(NULLABLE(OBJECT({contentUrl: STRING}))));
    }

    const onMouseEnter = () => {

      DEBUG: if (debug(video)) console.log('mouse enter', {video, preview});

      if (preview) {
        hover = video;
        video[LOOP] = previewMode == LOOP;
        load(video, `${new URL(preview.contentUrl, media.content.movies)}`);
      }
    };

    const onMouseLeave = () => {
      DEBUG: if (debug(video)) console.log('mouse leave', {video});

      if (hover == video) {
        hover = undefined;
      }

      stop();
    };

    ifAutoplayIsAllowed(video, (autoplayMode) => {
      watch(video, (video, list, previous) => {
        if (hover || !list || !previous) {
          autoplay = undefined;
          return;
        }

        const {head, first, center} = list;
        const firstIsFirst = head?.matches(':first-of-type');

        if ((!firstIsFirst && center == listElement) || (firstIsFirst && first == listElement)) {
          DEBUG: if (debug(video)) console.log('watch list position', {video, list, preview});

          if (preview) {
            autoplay = video;
            video[LOOP] = autoplayMode == LOOP;
            load(video, `${new URL(preview.contentUrl, media.content.movies)}`);
          }
        }
      });
    });

    on(listElement, MOUSEENTER, onMouseEnter);
    on(listElement, MOUSELEAVE, onMouseLeave);

    return () => {
      off(listElement, MOUSEENTER, onMouseEnter);
      off(listElement, MOUSELEAVE, onMouseLeave);
    };
  });

  return (element) => {
    if (user.underage) {
      remove(element);
    }

    compile(element);
  };
});
